import React, { useMemo } from "react";
import GlobalBrandStyle from "../../GlobalBrandStyle.js";
import notIframeStyles from "./ShopStylingWrapper.module.scss";
import iframeStyles from "./ShopStylingWrapperIframe.module.scss";
import classnames from "classnames";
import { LoadingComponent } from "rylak-common/src/components/loadingComponent/LoadingComponent.jsx";
import Head from "next/head";
import {
    getBackgroundImage,
    getBrandBackgroundFrameColor,
    getBrandBackgroundOpacity,
    getBrandButtonFont,
    getBrandFont,
    getBrandFrameBorderEffect,
    getGeneralBackgroundImage,
} from "~/cores/brandDataCore";
import { getLocalFont } from "./appUtils/fonts";
import Image from "next/image";
import _ from "lodash";
import { getProductsData, getSelectedProductValues, isConfirmPage, isLoginPage, isProductSelectionPage } from "~/cores/appCore";
import { serviceIsFetched } from "rylak-common";
import chroma from "chroma-js";
import { shouldBeThreeProductRows, shouldBeTwoProductRows } from "~/cores/productCore";

function urlIsVideo(url) {
    return url?.includes(".mp4") || url?.includes(".mp3") || url?.includes(".ogg") || url?.includes(".ogv") || url?.includes(".webm");
}

function BrandLogo({ homePage, logoSrc, imgClassName, styles }) {
    return (
        <a href={homePage} className={styles?.["logo-link"]} target={homePage && "_blank"}>
            <img src={logoSrc} alt={logoSrc} className={classnames(imgClassName, styles?.["logo"], homePage && styles?.["logo-clickable"])} />
        </a>
    );
}

function ShopStylingWrapper(props) {
    const { appProps, initServiceData, getLocaleText, brandData, children, appRef, appContainerRef } = props;

    const localFont = useMemo(() => {
        return getLocalFont(getBrandFont(appProps));
    }, [brandData]);

    const localButtonFont = useMemo(() => {
        return getLocalFont(getBrandButtonFont(appProps));
    }, [brandData]);

    const activeBackground = getBackgroundImage(appProps);
    const activeBackgroundIsVideo = activeBackground && urlIsVideo(activeBackground);
    const activeBackgroundImage = (!activeBackgroundIsVideo && activeBackground) || getGeneralBackgroundImage(props);

    const calculatedBackgroundImageOpacity = _.isNumber(getBrandBackgroundOpacity(appProps)) ? getBrandBackgroundOpacity(appProps) / 100 : 1;

    const isInIframe = appProps?.isInIframe;

    const isOnLoginPage = isLoginPage(appProps);

    const logoSrc = brandData?.logoSrc;
    const homePage = brandData?.homePage;

    const alternativeLogo = brandData?.alternativeLogoSrc;

    const backgroundFrameColor = chroma(getBrandBackgroundFrameColor(appProps)).alpha(calculatedBackgroundImageOpacity).css();

    const styles = isInIframe ? iframeStyles : notIframeStyles;

    const frameBorderEffect = getBrandFrameBorderEffect(appProps) || "DEFAULT";

    const twoProductsRowVisible =
        (isProductSelectionPage(appProps) && shouldBeTwoProductRows(getProductsData(appProps))) || (isConfirmPage(appProps) && shouldBeTwoProductRows(getSelectedProductValues(appProps)));

    return (
        <div
            className={classnames(localFont && `${localFont.variable}`, styles["background-on"])}
            ref={appRef}
            // style={{backgroundImage: (activeBackgroundImage) && `url('${activeBackgroundImage}')`}}
        >
            {activeBackgroundImage && !isInIframe && (
                <div
                    style={{
                        position: "fixed",
                        width: "100vw",
                        height: "100vh",
                        top: 0,
                        left: 0,
                    }}
                >
                    <Image priority={true} fill style={{ objectFit: "cover" }} alt={"site-background-image"} src={activeBackgroundImage} />
                </div>
            )}
            {serviceIsFetched(initServiceData) && (
                <Head>
                    <title>{initServiceData?.data?.htmlPageTitle || "Ingrid returns"}</title>
                    {initServiceData?.data?.htmlFavicon ? (
                        <>
                            <link rel="apple-touch-icon" href={initServiceData.data?.htmlFavicon} />
                            <link rel="icon" type="image/png" sizes="32x32" href={initServiceData.data?.htmlFavicon} />
                            <link rel="icon" type="image/png" sizes="16x16" href={initServiceData.data?.htmlFavicon} />
                        </>
                    ) : (
                        <>
                            <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
                            <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
                            <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
                            <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#5bbad5" />
                            <meta name="msapplication-TileColor" content="#da532c" />
                            <meta name="theme-color" content="#ffffff" />
                        </>
                    )}
                </Head>
            )}
            {!isInIframe && (
                <video
                    style={{
                        position: "fixed",
                        right: 0,
                        bottom: 0,
                        left: 0,
                        opacity: activeBackgroundIsVideo ? 1 : 0,
                        top: 0,
                        objectFit: "cover",
                        width: "100vw",
                        height: "100vh",
                    }}
                    playsInline
                    autoPlay
                    muted
                    loop
                >
                    <source src={activeBackground} type="video/webm" />
                    Your browser does not support the video tag.
                </video>
            )}
            <GlobalBrandStyle
                appProps={appProps}
                {...brandData}
                color={brandData?.colorPrimary || "#343a40"}
                fontName={(localFont && `${localFont.style.fontFamily}`) || brandData?.fontNamePrimary}
                buttonFont={(localButtonFont && `${localButtonFont.style.fontFamily}`) || brandData?.buttonFont}
                fontSizeBody={brandData?.fontSizeBody || "14px"}
                fontWeightBody={brandData?.fontWeightBody || "normal"}
                uniformText={brandData?.uniformText || false}
            />
            <div
                className={classnames(
                    styles["app-container-wrapper"],
                    isOnLoginPage && styles["login-page"],
                    // isInIframe && styles["app-container-wrapper-iframe"],
                    // isInIframe && isOnLoginPage && styles["login-page-iframe"],
                )}
            >
                <div
                    ref={appContainerRef}
                    className={classnames(
                        styles["app-container"],
                        isOnLoginPage && styles["app-container-login"],
                        twoProductsRowVisible && styles["app-container-two-product-rows"],

                        // isInIframe && styles["app-container-iframe"],
                    )}
                >
                    <div
                        className={classnames(styles["app-container-inner"], !isInIframe && styles["frame-border-effect-" + frameBorderEffect])}
                        style={{
                            backgroundColor: backgroundFrameColor,
                            backdropFilter: `blur(${brandData?.backgroundImageBlur}px)`,
                            // boxShadow: !isInIframe && backgroundBoxShadowActive && "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                        }}
                    />

                    {!isInIframe && (
                        <div className={classnames(styles["logo-and-language"], !isOnLoginPage && styles["logo-and-language-not-login-page"])}>
                            <div style={{ position: "relative" }}>
                                {logoSrc && (
                                    <div className={isOnLoginPage && alternativeLogo && styles["logo-hide-mobile"]}>
                                        <BrandLogo styles={styles} homePage={homePage} logoSrc={logoSrc} imgClassName={styles["brand-logo-src"]} />
                                    </div>
                                )}
                                {alternativeLogo && isOnLoginPage && (
                                    <div className={isOnLoginPage && alternativeLogo && styles["logo-show-mobile"]} style={{ position: "absolute", top: 0 }}>
                                        <BrandLogo styles={styles} homePage={homePage} logoSrc={alternativeLogo} imgClassName={styles["brand-logo-src-mobile-login"]} />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {!serviceIsFetched(initServiceData) ? <LoadingComponent getLocaleText={getLocaleText} /> : <>{children}</>}
                </div>
            </div>
        </div>
    );
}

export default ShopStylingWrapper;
